import { cargarGeocodificacion, cargarGoogleMaps, hayLlaveDeGoogle } from '@/lib/mapas/google'

/**
 * Geocodificación con Google.
 *
 * ATENCIÓN, y que quede donde se lee: esto MANDA LA DIRECCIÓN DEL CLIENTE a
 * Google. MEGA lo autorizó el 15 de agosto de 2026, después de comprobar que
 * OpenStreetMap no encuentra ninguna de sus direcciones con nombre de sitio.
 * No se manda nada más: ni el nombre del cliente, ni su documento, ni el
 * valor del trabajo. Solo la dirección y el municipio.
 *
 * Se usa el geocodificador del navegador y no el servicio web, que es lo que
 * Google indica para aplicaciones de navegador: una sola llave, sin problemas
 * de CORS y con el mismo cobro.
 */

export type Ubicacion = {
  latitud: number
  longitud: number
  /** Qué tan fino: ROOFTOP, RANGE_INTERPOLATED, GEOMETRIC_CENTER, APPROXIMATE. */
  precision: string
  /** La dirección tal como Google la entendió. Sirve para desconfiar. */
  texto: string
}

export type Direccion = {
  direccion: string
  ciudad: string
  departamento: string
}

let geocodificador: google.maps.Geocoder | null = null

/**
 * Busca una dirección.
 *
 * Se pregunta con el municipio y el departamento pegados, y con el país
 * acotado: en Colombia hay una Calle 19 en cada pueblo, y sin acotar Google
 * escoge la de la ciudad más grande.
 *
 * Devuelve null cuando no encuentra nada, que es distinto de fallar: una
 * dirección que no existe no es un error del sistema.
 */
export async function buscarDireccion(donde: Direccion): Promise<Ubicacion | null> {
  // Si lo escrito ya son coordenadas («4.753987,-75.945847»), son el punto:
  // a Google no se le pregunta. La OS-2026-0104 nació así y Google, que no
  // entiende coordenadas como dirección, devolvió el centro de Colombia.
  const coordenadas = coordenadasDe(donde.direccion)
  if (coordenadas) return coordenadas

  const api = await cargarGoogleMaps()

  if (!geocodificador) {
    geocodificador = new api.geocoding.Geocoder()
  }

  const consulta = [donde.direccion, donde.ciudad, donde.departamento, 'Colombia']
    .map((parte) => parte?.trim())
    .filter(Boolean)
    .join(', ')

  try {
    const { results } = await geocodificador.geocode({
      address: consulta,
      componentRestrictions: { country: 'CO' },
    })

    const primero = results[0]
    if (!primero) return null

    return {
      latitud: primero.geometry.location.lat(),
      longitud: primero.geometry.location.lng(),
      precision: String(primero.geometry.location_type ?? 'APPROXIMATE'),
      texto: primero.formatted_address ?? '',
    }
  } catch (fallo) {
    const codigo = (fallo as { code?: string })?.code

    // ZERO_RESULTS no es una falla: es que no existe. Se distingue para no
    // parar la cola entera por una dirección mal escrita.
    if (codigo === 'ZERO_RESULTS') return null

    if (codigo === 'OVER_QUERY_LIMIT') {
      throw new Error('Google pidió esperar: se pasó del cupo de consultas.')
    }

    if (codigo === 'REQUEST_DENIED') {
      throw new Error(
        'Google rechazó la consulta. Revise que la llave tenga habilitada la API de Geocoding y que el dominio esté permitido.',
      )
    }

    throw fallo instanceof Error ? fallo : new Error('Falló la búsqueda en Google.')
  }
}

/**
 * LA DIRECCIÓN A PARTIR DE UNAS COORDENADAS (geocodificación inversa).
 *
 * Es lo que se le quema a la foto de evidencia: en vez de «10°..N 75°..W»,
 * que no le dice nada a nadie, la dirección que Google reconoce en ese punto.
 * Manda a Google SOLO el par de coordenadas —ni cliente, ni documento, ni
 * valor—, y es lo mismo que ya hace el mapa del técnico.
 *
 * Nunca lanza: si no hay llave, si Google la rechaza, si no hay red o si no
 * reconoce el sitio, devuelve null y la foto se estampa igual con las
 * coordenadas. Una evidencia con coordenadas es mejor que ninguna.
 */
export async function direccionDesdeCoords(
  latitud: number,
  longitud: number,
): Promise<string | null> {
  if (!hayLlaveDeGoogle()) return null

  try {
    const geo = await cargarGeocodificacion()
    if (!geocodificador) {
      geocodificador = new geo.Geocoder()
    }

    // UN REINTENTO ANTES DE RENDIRSE. En obra la señal es mala y la primera
    // llamada se cae por un blip de red o un OVER_QUERY_LIMIT momentáneo; sin
    // reintento eso deja la foto con los grados en vez de la dirección. Dos
    // intentos con una pausa corta atrapan casi todas esas fallas pasajeras.
    let texto: string | undefined
    for (let intento = 0; intento < 2 && !texto; intento++) {
      if (intento > 0) await new Promise((r) => setTimeout(r, 700))
      try {
        const { results } = await geocodificador.geocode({
          location: { lat: latitud, lng: longitud },
        })
        texto = results[0]?.formatted_address
      } catch {
        // Se deja para el siguiente intento; si el segundo también falla, sale
        // por el catch de afuera y devuelve null.
        if (intento === 1) throw new Error('geocode falló')
      }
    }
    if (!texto) return null

    // Sin la coletilla «, Colombia» ni el código postal: en la foto ocupan una
    // línea entera para un dato que ya se sabe. Queda «Cra. 4 #10-20, Cartago,
    // Valle del Cauca».
    return texto
      .replace(/,?\s*colombia\s*$/i, '')
      .replace(/\b\d{6}\b/g, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/,\s*,/g, ',')
      .replace(/,\s*$/, '')
      .trim()
  } catch {
    return null
  }
}

/**
 * ADELANTA LA CARGA DE LA GEOCODIFICACIÓN.
 *
 * La librería de Google se descarga una vez y se queda en memoria. Si se pide
 * al montar la pantalla de fotos —cuando el técnico todavía está encuadrando—,
 * para cuando dispara ya está lista y la dirección llega en el acto, en vez de
 * competir contra el tope de tiempo. Nunca lanza: si falla, la foto se estampa
 * igual con las coordenadas.
 */
export function precargarGeocodificador(): void {
  if (!hayLlaveDeGoogle()) return
  void cargarGeocodificacion()
    .then((geo) => {
      if (!geocodificador) geocodificador = new geo.Geocoder()
    })
    .catch(() => {})
}

/**
 * Qué tan de fiar es el punto.
 *
 * ROOFTOP es la casa; RANGE_INTERPOLATED es la cuadra, calculada entre dos
 * números conocidos. Los otros dos son el centro de algo más grande, y
 * dibujarlos igual que una dirección exacta sería mentir con un punto.
 */
export function esPuntoFino(precision: string): boolean {
  return precision === 'ROOFTOP' || precision === 'RANGE_INTERPOLATED' || precision === COORDENADAS
}

/** Precisión de un punto que se escribió como coordenadas: es exacto. */
const COORDENADAS = 'COORDENADAS'

/**
 * «4.753987,-75.945847» (o con espacio o punto y coma) → el punto, si cae en
 * Colombia. Cualquier otra cosa → null, y se busca como dirección.
 */
function coordenadasDe(texto: string | null | undefined): Ubicacion | null {
  const m = (texto ?? '').trim().match(/^(-?\d{1,2}\.\d{3,})\s*[,;\s]\s*(-?\d{1,3}\.\d{3,})$/)
  if (!m) return null
  const latitud = Number(m[1])
  const longitud = Number(m[2])
  if (latitud < -5 || latitud > 14 || longitud < -82 || longitud > -66) return null
  return { latitud, longitud, precision: COORDENADAS, texto: `${latitud}, ${longitud}` }
}

/**
 * ¿ES DE FIAR ESTE PUNTO PARA MANDAR UN TÉCNICO?
 *
 * Esta comprobación faltaba, y su ausencia costó caro: de 24 órdenes
 * geocodificadas, 18 no estaban en una dirección. Catorce cayeron en el CENTRO
 * EXACTO de Cartago —el mismo par de coordenadas, trece veces— porque cuando
 * Google no encuentra la dirección no dice «no sé»: devuelve el municipio con
 * precisión APPROXIMATE, y eso se guardaba como si fuera la casa.
 *
 * Un punto equivocado es peor que ningún punto. Sin punto, el mapa lo dice y
 * alguien lo ubica a mano. Con un punto falso, el mapa se ve completo, el
 * técnico sale hacia allá, y de paso el reparto ordena por cercanía usando una
 * distancia que no existe.
 *
 * Se rechazan tres cosas:
 *
 *   1. LA PRECISIÓN FLOJA. Solo valen ROOFTOP y RANGE_INTERPOLATED: la casa, o
 *      la cuadra calculada entre dos números conocidos. GEOMETRIC_CENTER es el
 *      centro de algo más grande y APPROXIMATE es un encogimiento de hombros.
 *
 *   2. EL MUNICIPIO CAMBIADO. «Estadio de Caicedonia» acabó en Cartago porque
 *      la consulta añade el municipio por defecto. Si lo que devuelve Google no
 *      nombra el municipio que se pidió, no es el sitio.
 *
 *   3. EL NÚMERO SUSTITUIDO. Es el más traicionero: se pidió «Calle 10 # 21-46»
 *      y Google devolvió «Cl. 10 # 7-21» marcándolo ROOFTOP, o sea con la
 *      confianza máxima. No falló: CORRIGIÓ la dirección por una que sí
 *      conoce, y sin comparar no hay forma de notarlo.
 */
export type Veredicto = { sirve: true } | { sirve: false; motivo: string }

/** Los números de una dirección, en orden. Es con lo que se compara. */
function numerosDe(texto: string): string[] {
  return (texto.match(/\d+/g) ?? []).filter((n) => n.length <= 5)
}

export function puntoDeFiar(punto: Ubicacion, pedido: Direccion): Veredicto {
  // Coordenadas escritas a mano: son el punto, no hay nada que comparar.
  if (punto.precision === COORDENADAS) return { sirve: true }
  if (!esPuntoFino(punto.precision)) {
    return {
      sirve: false,
      motivo:
        punto.precision === 'APPROXIMATE'
          ? 'Google no encontró la dirección: devolvió el municipio.'
          : 'Google devolvió el centro de una zona, no la dirección.',
    }
  }

  const respuesta = normalizarTexto(punto.texto)

  const municipio = normalizarTexto(pedido.ciudad ?? '')
  if (municipio && !respuesta.includes(municipio)) {
    return {
      sirve: false,
      motivo: `La dirección encontrada no está en ${pedido.ciudad}, sino en «${punto.texto}».`,
    }
  }

  // Los números que se pidieron tienen que aparecer en lo que devolvió. Si se
  // pidieron dos —calle y placa— basta con que estén los dos, en cualquier
  // orden: Google los reescribe pero no los inventa cuando acierta.
  // Solo los tres de la nomenclatura —vía, número y placa—. Lo que venga
  // después («NIVEL P4», «casa 04», «local 102») Google no lo devuelve, y
  // exigirlo descartaba direcciones bien encontradas (OS-2026-0102).
  const pedidos = numerosDe(pedido.direccion).slice(0, 3)
  const devueltos = numerosDe(punto.texto)

  if (pedidos.length >= 2) {
    const faltan = pedidos.filter((n) => !devueltos.includes(n))
    if (faltan.length > 0) {
      return {
        sirve: false,
        motivo: `Se pidió «${pedido.direccion}» y Google devolvió «${punto.texto}»: no es la misma dirección.`,
      }
    }
  }

  return { sirve: true }
}

/** Sin tildes, sin mayúsculas y sin puntuación: para comparar, no para mostrar. */
function normalizarTexto(t: string): string {
  return t
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
